import { css } from "@emotion/react";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FlagIcon from "@mui/icons-material/Flag";
import { Table } from "../Table";
import { Tooltip } from "../Tooltip";
import { useTheme } from "../theme";
import { CSVImportUIColumns } from "./CSVImportUI";

export interface CSVImportUITableProps<Row extends Record<string, string>> {
  rows: Row[];
  errors: ([string, string][] | undefined)[];
  headers: string[];
  renderCell?: (row: Row, header: string) => React.ReactNode;
  columns?: CSVImportUIColumns;
}

export const CSVImportUITable = <Row extends Record<string, string>>({
  rows,
  errors,
  headers,
  columns,
}: CSVImportUITableProps<Row>) => {
  const theme = useTheme();
  return (
    <div
      css={css`
        width: 100%;
        overflow: scroll;
      `}
    >
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell />
            {headers.map((row, index) => (
              <Table.HeaderCell align="left" key={index}>
                {row}
              </Table.HeaderCell>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body striped>
          {rows.map((row, index) => {
            const rowErrors = errors[index];
            const hasRowErrors = (rowErrors && rowErrors.length > 0) ?? false;
            return (
              <Table.Row
                data-row-invalid={hasRowErrors}
                key={index}
                css={
                  hasRowErrors
                    ? css`
                        td {
                          background-color: ${theme.palette.intentions.error
                            .light} !important;
                        }
                      `
                    : null
                }
              >
                <Table.Cell>
                  {hasRowErrors ? <ErrorOutlineIcon color="error" /> : null}
                </Table.Cell>
                {headers.map((header) => {
                  const error = rowErrors?.find(
                    (errors) => errors?.[0] === header,
                  );
                  const RenderCell = columns?.[header]?.renderCell;
                  const value = row[header];
                  return (
                    <Table.Cell key={header}>
                      {error ? (
                        <Tooltip title={error[1]} arrow>
                          <FlagIcon color="error" />
                        </Tooltip>
                      ) : null}
                      {RenderCell ? (
                        <RenderCell value={value} row={row} header={header} />
                      ) : (
                        value
                      )}
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
};
